import React, { useState } from "react";
import {
  CButton,
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CSpinner,
} from "@coreui/react";
import type { ApiExecution, ExecutionStopMode } from "@tradingdroid/frontends-types";

import { useConfirmModal } from "@src/hooks/useConfirmModal";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import { getApiErrorMessage } from "@src/utils/apiError";
import {
  canForceRecover,
  canRepairHaltedStop,
  canResolveSafetyStop,
  canResumeDesired,
  isSafetyHalted,
} from "@src/utils/executionStatus";

interface Props {
  execution: ApiExecution;
  onActionComplete: () => Promise<boolean>;
}

/**
 * Operator-facing recovery actions for an execution with no live worker (suspended / crashed /
 * halted / stopped). Recover forces the orchestrator to re-claim a crashed or halted row the operator
 * still wants running (desired_state 'running'); Resume flips a stranded stopped `crashed` row back to
 * running, and is not offered for a halted one — that goes through Repair Stop; Stop admits a
 * mode-aware terminal stop. A no-worker close_market stop returns a bookkeeping-halt (repair
 * required) instead of ending the execution — surfaced as a warning, with no navigation away.
 *
 * A safety halt (suspend_cause_name 'safety') gets neither Stop nor Repair Stop. The backend refuses
 * both there: the stop-marker CAS, the no-worker terminalizer and the repair-stop CAS each fence out
 * that cause, because their terminal writes clear it and would close the books on an execution a
 * recovery attempt judged unsafe to resume. Which exit it does get depends on what the operator wants
 * the execution to do. Still running: Recover, which clears the cause and hands the row back to the
 * orchestrator. Stopped: Resolve Safety Stop, whose own CAS finalizes it — the note beside the
 * withheld Stop is therefore derived from which of those buttons actually rendered, rather than
 * assuming one of them did.
 */
export const ExecutionOperatorActions: React.FC<Props> = ({ execution, onActionComplete }) => {
  const [busy, setBusy] = useState<
    null | "recover" | "resume" | "repair" | "resolveSafetyStop" | "stop"
  >(null);
  // A refused action leaves the screen showing the row the server declined to act on, and the
  // refresh is what replaces it. When that refresh does not land, the controls stay withheld instead
  // of coming back over the stale row: a second Recover would re-read the execution as it has since
  // become, and a safety halt is an admitted shape for recovery, so that attempt would clear the very
  // cause the first one was refused for. Latched until a refresh positively reports a fresh row.
  const [staleAfterRefresh, setStaleAfterRefresh] = useState(false);

  // Never throws: the caller runs this from a finally, where a rejection would skip the busy reset
  // and strand every control disabled. A refresh that rejects and one that reports no fresh row are
  // the same situation for the operator, so both latch.
  const refreshAfterAction = async (): Promise<void> => {
    try {
      setStaleAfterRefresh((await onActionComplete()) !== true);
    } catch {
      setStaleAfterRefresh(true);
    }
  };
  const { showToast } = useToastActions();
  const { confirmModal } = useConfirmModal();

  const safetyHalted = isSafetyHalted(execution);
  const showRecover = canForceRecover(execution);
  const showResume = canResumeDesired(execution);
  const showRepair = canRepairHaltedStop(execution);
  const showResolveSafetyStop = canResolveSafetyStop(execution);

  const handleRecover = async () => {
    // Recovery discards a pending stop request, because the marker blocks the recovery claim and would
    // otherwise let the pending-stop reaper terminalize the row instead. Say so before the click as
    // well as after it, when the row is already carrying one.
    const pendingStop = execution.stop_request_name;
    const { confirmed } = await confirmModal(
      `Recover execution #${execution.id}?\n\nThe orchestrator will re-acquire the lease and resume normal operation.${
        pendingStop
          ? `\n\nThis execution has a pending "${pendingStop}" stop request. Recovering CANCELS it — the execution goes back to running instead of stopping.`
          : ""
      }`,
      { title: "Recover Execution" },
    );
    if (!confirmed) return;

    setBusy("recover");
    try {
      const result = await tdApi.recoverExecution(execution.id);
      if (result?.triggered) {
        showToast({
          type: "success",
          title: "Recovery Triggered",
          message: `Execution #${execution.id} recovery initiated; the orchestrator will re-claim it.${
            result.cleared_stop_request
              ? ` The pending "${result.cleared_stop_request}" stop request was cancelled.`
              : ""
          }`,
          timeout: 8000,
        });
      } else {
        showToast({
          type: "warning",
          title: "Recovery",
          message: "Recovery request sent but response was unexpected.",
          timeout: 8000,
        });
      }
    } catch (err) {
      // A refused recovery is a 409 carrying the server's own explanation of what the row became.
      // Show that instead of the transport's "Request failed with status code 409".
      showToast({
        type: "danger",
        title: "Recovery Failed",
        message: getApiErrorMessage(err),
        timeout: 8000,
      });
    } finally {
      // Refresh on BOTH paths, not only on success. Recovery is a compare-and-swap on the state the
      // server read, so a refusal means the row moved — which makes the version on screen the stale
      // one, and re-enabling the button over it invites a second click that acts on what the first
      // was refused for.
      await refreshAfterAction();
      setBusy(null);
    }
  };

  const handleResume = async () => {
    const { confirmed } = await confirmModal(
      `Resume execution #${execution.id}?\n\nThe orchestrator will re-claim it and bring it back to running.`,
      { title: "Resume Execution" },
    );
    if (!confirmed) return;

    setBusy("resume");
    try {
      const result = await tdApi.resumeExecutionDesired(execution.id);
      if (result?.resumed) {
        showToast({
          type: "success",
          title: "Resume Requested",
          message: `Execution #${execution.id} will be re-claimed by the orchestrator.`,
          timeout: 8000,
        });
      } else {
        showToast({
          type: "warning",
          title: "Resume",
          message: "Resume request sent but response was unexpected.",
          timeout: 8000,
        });
      }
    } catch (err) {
      // A refused resume is a 409 carrying the server's own account of the row shape it required.
      // Show that instead of the transport's "Request failed with status code 409".
      showToast({
        type: "danger",
        title: "Resume Failed",
        message: getApiErrorMessage(err),
        timeout: 8000,
      });
    } finally {
      await refreshAfterAction();
      setBusy(null);
    }
  };

  const handleRepair = async () => {
    const { confirmed } = await confirmModal(
      `Finalize the halted stop for execution #${execution.id}?\n\nThis execution was halted because its positions could not be verified flat when it stopped. Before repairing it, confirm on the exchange that exposure is zero AND that every order this execution owns is cancelled: a position that was not booked closed keeps its resting take-profit / stop-loss orders. This closes the books: it marks the execution stopped and releases its wallet-symbol ownership, so another bot can claim the same symbol-side — and a leftover order would then act against that bot.`,
      { title: "Repair Stop" },
    );
    if (!confirmed) return;

    setBusy("repair");
    try {
      const result = await tdApi.repairHaltedStop(execution.id);
      if (result?.committed) {
        showToast({
          type: "success",
          title: "Stop Repaired",
          message: `Execution #${execution.id} finalized as stopped.`,
          timeout: 8000,
        });
      } else {
        showToast({
          type: "warning",
          title: "Repair",
          message: "Repair request sent but response was unexpected.",
          timeout: 8000,
        });
      }
    } catch (err) {
      // A refused repair is a 409 carrying the row's status and the shape this route finalizes, which
      // is more than the old committed=false body could say: that one reported the status alone, so the
      // reasons had to be guessed at here. Show the server's text.
      showToast({
        type: "danger",
        title: "Repair Failed",
        message: getApiErrorMessage(err),
        timeout: 8000,
      });
    } finally {
      await refreshAfterAction();
      setBusy(null);
    }
  };

  const handleResolveSafetyStop = async () => {
    // This is the one action that closes the books on a row a recovery attempt judged unsafe to
    // resume, so the note is mandatory and is stored on the execution: it is the audit trail for a
    // wallet-symbol ownership release no worker certified. The same exchange checks Repair Stop asks
    // for apply, and for the same reason — a position that was never booked closed keeps its resting
    // take-profit / stop-loss orders, which would act against whichever bot claims the symbol-side next.
    const { confirmed, promptValue } = await confirmModal(
      `Finalize the safety-halted execution #${execution.id} as stopped?\n\nA recovery attempt judged this execution unsafe to resume, and you have set its desired state to "stopped". Before finalizing, confirm on the exchange that exposure is zero AND that every order this execution owns is cancelled. This closes the books: it clears the safety cause, marks the execution stopped and releases its wallet-symbol ownership, so another bot can claim the same symbol-side. If you want the execution back instead, set desired state to "running" and use Recover.`,
      {
        title: "Resolve Safety Stop",
        confirmLabel: "Finalize as Stopped",
        confirmColor: "danger",
        promptConfig: {
          type: "text",
          label: "What did you reconcile?",
          placeholder: "e.g. Binance BTC/USDT: no position, no open orders",
        },
      },
    );
    if (!confirmed) return;
    const note = (promptValue ?? "").trim();
    if (!note) {
      showToast({
        type: "warning",
        title: "Note Required",
        message: "Describe what you reconciled — it is stored on the execution as the audit trail.",
        timeout: 8000,
      });
      return;
    }

    setBusy("resolveSafetyStop");
    try {
      const result = await tdApi.resolveExecutionSafetyStop(execution.id, note);
      if (result?.committed) {
        showToast({
          type: "success",
          title: "Safety Stop Resolved",
          message: `Execution #${execution.id} finalized as stopped; its wallet-symbol ownership was released.`,
          timeout: 8000,
        });
      } else {
        showToast({
          type: "warning",
          title: "Resolve Safety Stop",
          message: "Resolve request sent but response was unexpected.",
          timeout: 8000,
        });
      }
    } catch (err) {
      // A refused CAS arrives here as a 409 naming the row shape this route finalizes, where it used
      // to arrive as a 200 body this component had to compose an explanation from. A rejected operator
      // note and an authorization failure land here too, so this reads the server's own text rather
      // than the transport's status line, which says only that the request failed.
      showToast({
        type: "danger",
        title: "Resolve Failed",
        message: getApiErrorMessage(err),
        timeout: 8000,
      });
    } finally {
      await refreshAfterAction();
      setBusy(null);
    }
  };

  // Stands in for the Stop dropdown on a safety halt, which renders neither mode: with a safety cause
  // set, the marker CAS and the no-worker terminalizer both refuse the row, so the request would come
  // back rejected. Which note is shown depends on which of the two exits this row actually renders —
  // pointing at a button that is not there leaves the operator with an instruction that does not
  // apply. Recover is also not an exchange-reconciliation step: the halt's reason code can be a
  // capital shortfall, an ownership conflict, a repeated-failure budget or an application error just
  // as easily.
  const renderSafetyHaltNote = () => {
    if (showResolveSafetyStop) {
      return (
        <span className="ExecutionOperatorActions-safetyStopNote text-danger small">
          Safety halt — the Stop modes are refused. Reconcile the exchange, then use Resolve Safety
          Stop to close the books.
        </span>
      );
    }
    if (showRecover) {
      return (
        <span className="ExecutionOperatorActions-safetyNote text-danger small">
          Safety halt — stopping is refused. Resolve what the halt reason records, then use Recover.
        </span>
      );
    }
    return (
      <span className="ExecutionOperatorActions-safetyNoActionNote text-danger small">
        Safety halt with desired_state &quot;{execution.desired_state}&quot; and status &quot;
        {execution.status_name}&quot; — no operator action available here. Stop and Repair Stop are
        refused for a safety cause, Recover admits only a desired_state of &quot;running&quot;, and
        Resolve Safety Stop admits only an open halt with desired_state &quot;stopped&quot;.
      </span>
    );
  };

  const handleStop = async (mode: ExecutionStopMode) => {
    const closing = mode === "close_market";
    const { confirmed } = await confirmModal(
      closing
        ? "Request a close-and-stop for this execution?\n\nThis records the request; it does not itself close anything, and no worker reads it. With a worker attached, End & Close All is the command that ATTEMPTS a market close — it is an attempt, not a guarantee, so verify on the exchange afterwards. Whenever the positions cannot be verified closed — no worker attached, or a worker that runs the close and cannot account for a position — the execution is HALTED for bookkeeping repair instead of ended."
        : "Stop this execution as-is, leaving any open positions and orders untouched on the exchange?",
      { title: closing ? "Stop & Close Market" : "Stop (Leave As-Is)" },
    );
    if (!confirmed) return;

    setBusy("stop");
    try {
      const result = await tdApi.requestExecutionStop(execution.id, mode);
      if (!result?.admitted) {
        // The dropdown is withheld for a safety halt, so reaching this reason means the row acquired
        // the cause between render and click. Spell it out rather than printing the raw code.
        const refusal =
          result?.reason === "safety_halt"
            ? "This execution is safety-halted: a recovery attempt judged it unsafe to resume, so stopping is refused. Read the row's suspend reason code and last_error to see what has to be resolved, then use Recover."
            : result?.reason;
        showToast({
          type: "warning",
          title: "Stop Not Admitted",
          message:
            refusal ||
            `Execution is not in a stoppable state${
              result?.current_status ? ` (status: ${result.current_status})` : ""
            }.`,
          timeout: 8000,
        });
      } else if (result.result === "bookkeeping_halt") {
        // No live worker to prove positions flat: the row is halted (end_ts still null), not ended.
        showToast({
          type: "warning",
          title: "Halted — Repair Required",
          message:
            "No live worker to close positions. The execution was halted for bookkeeping repair; confirm the exchange has no open exposure and no orders left resting for this execution, then use Repair Stop to finalize it.",
          timeout: 15000,
        });
      } else if (result.result === "stopped") {
        showToast({
          type: "success",
          title: "Execution Stopped",
          message: `Execution #${execution.id} stopped.`,
          timeout: 8000,
        });
      } else {
        // A live worker holds the lease, so the API only recorded a pending marker (mode:
        // live/pending). No worker code reads that column — its effect is to stop the execution being
        // re-claimed or made ready. Stopping a running worker is a separate action, the End Only /
        // End & Close All execution commands, so the message must not promise the worker will act.
        showToast({
          type: "info",
          title: "Stop Requested",
          message: closing
            ? "A pending stop was recorded; it prevents the execution being re-claimed. It does not reach the running worker — use End & Close All to make it flatten and stop."
            : "A pending stop was recorded; it prevents the execution being re-claimed. It does not reach the running worker — use End Only to make it stop.",
          timeout: 12000,
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      showToast({ type: "danger", title: "Stop Failed", message, timeout: 8000 });
    } finally {
      await refreshAfterAction();
      setBusy(null);
    }
  };

  return (
    <div className="ExecutionOperatorActions d-flex align-items-center gap-2">
      {showRecover && (
        <CButton
          className="ExecutionOperatorActions-recover"
          color="info"
          size="sm"
          onClick={handleRecover}
          disabled={busy !== null || staleAfterRefresh}
        >
          {busy === "recover" ? <CSpinner size="sm" /> : "Recover"}
        </CButton>
      )}
      {showResume && (
        <CButton
          className="ExecutionOperatorActions-resume"
          color="success"
          size="sm"
          onClick={handleResume}
          disabled={busy !== null || staleAfterRefresh}
        >
          {busy === "resume" ? <CSpinner size="sm" /> : "Resume"}
        </CButton>
      )}
      {showRepair && (
        <CButton
          className="ExecutionOperatorActions-repair"
          color="warning"
          size="sm"
          onClick={handleRepair}
          disabled={busy !== null || staleAfterRefresh}
        >
          {busy === "repair" ? <CSpinner size="sm" /> : "Repair Stop"}
        </CButton>
      )}
      {showResolveSafetyStop && (
        <CButton
          className="ExecutionOperatorActions-resolveSafetyStop"
          color="danger"
          size="sm"
          onClick={handleResolveSafetyStop}
          disabled={busy !== null || staleAfterRefresh}
        >
          {busy === "resolveSafetyStop" ? <CSpinner size="sm" /> : "Resolve Safety Stop"}
        </CButton>
      )}
      {safetyHalted ? (
        // Withheld while the row is stale: this note names Recover, which the latch has disabled.
        !staleAfterRefresh && renderSafetyHaltNote()
      ) : (
        <CDropdown className="ExecutionOperatorActions-stop">
          <CDropdownToggle
            color="warning"
            variant="outline"
            size="sm"
            disabled={busy !== null || staleAfterRefresh}
          >
            {busy === "stop" ? <CSpinner size="sm" /> : "Stop"}
          </CDropdownToggle>
          <CDropdownMenu>
            <CDropdownItem className="cursor-pointer" onClick={() => handleStop("close_market")}>
              Close Market &amp; Stop
            </CDropdownItem>
            <CDropdownItem className="cursor-pointer" onClick={() => handleStop("no_close")}>
              Stop (Leave As-Is)
            </CDropdownItem>
          </CDropdownMenu>
        </CDropdown>
      )}
      {staleAfterRefresh && (
        <span className="ExecutionOperatorActions-staleNotice text-danger small">
          Could not reload this execution. The actions are withheld because this view may no longer
          match the server. Reload the page.
        </span>
      )}
    </div>
  );
};
